import { createReducer, on } from '@ngrx/store';
import { CapitoliActions } from './capitoli.actions';
import {CantiereAndamento, Capitolo, Extra, Incasso} from '../../core/models/capitoli.model';

export interface CapitoliState {
  items: Capitolo[];
  loading: boolean;
  error: string | null;

  selected: Capitolo | null;

  opLoading: boolean;
  opError: string | null;

  cantiereAndamenti: CantiereAndamento[];
  cantiereAndamentiLoading: boolean;
  cantiereAndamentiError: string | null;

  extras: Extra[];
  extrasLoading: boolean;
  extrasError: string | null;

  incassi: Incasso[];
  incassiLoading: boolean;
  incassiError: string | null;
}

export const initialState: CapitoliState = {
  items: [],
  loading: false,
  error: null,

  selected: null,

  opLoading: false,
  opError: null,

  cantiereAndamenti: [],
  cantiereAndamentiLoading: false,
  cantiereAndamentiError: null,


  extras: [],
  extrasLoading: false,
  extrasError: null,

  incassi: [],
  incassiLoading: false,
  incassiError: null,
};

// Helper per confronti sicuri tra ID
const toId = (v: unknown) => String(v ?? '');

export const capitoliReducer = createReducer(
  initialState,

  on(CapitoliActions.loadByPreventivoId, (s) => ({ ...s, loading: true, error: null })),
  on(CapitoliActions.loadByPreventivoIdSuccess, (s, { items }) => ({
    ...s,
    loading: false,
    items,
  })),
  on(CapitoliActions.loadByPreventivoIdFailure, (s, { error }) => ({ ...s, loading: false, error })),

  on(CapitoliActions.loadDetails, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CapitoliActions.loadDetailsSuccess, (s, { item }) => ({ ...s, opLoading: false, selected: item })),
  on(CapitoliActions.loadDetailsFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(CapitoliActions.create, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CapitoliActions.createSuccess, (s) => ({ ...s, opLoading: false })),
  on(CapitoliActions.createFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(CapitoliActions.update, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CapitoliActions.updateSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    // Non aggiorniamo items/selected perché abbiamo solo l'ID, faremo il reload
  })),
  on(CapitoliActions.updateFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),

  on(CapitoliActions.delete, (s) => ({ ...s, opLoading: true, opError: null })),
  on(CapitoliActions.deleteSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    items: s.items.filter((x) => toId(x.id) !== toId(id)),
    selected: toId(s.selected?.id) === toId(id) ? null : s.selected,
  })),
  on(CapitoliActions.deleteFailure, (s, { error }) => ({ ...s, opLoading: false, opError: error })),
  on(CapitoliActions.loadByCantiereAndamentoId, (s) => ({
    ...s,
    cantiereAndamentiLoading: true,
    cantiereAndamentiError: null,
  })),

  on(CapitoliActions.loadByCantiereAndamentoIdSuccess, (s, { items }) => ({
    ...s,
    cantiereAndamentiLoading: false,
    cantiereAndamenti: items,
  })),

  on(CapitoliActions.loadByCantiereAndamentoIdFailure, (s, { error }) => ({
    ...s,
    cantiereAndamentiLoading: false,
    cantiereAndamentiError: error,
  })),

  on(
    CapitoliActions.createCantiereAndamento,
    CapitoliActions.updateCantiereAndamento,
    CapitoliActions.deleteCantiereAndamento,
    (s) => ({
      ...s,
      opLoading: true,
      opError: null,
    })
  ),

  on(
    CapitoliActions.createCantiereAndamentoSuccess,
    CapitoliActions.updateCantiereAndamentoSuccess,
    (s) => ({
      ...s,
      opLoading: false,
    })
  ),

  on(CapitoliActions.deleteCantiereAndamentoSuccess, (s, { id }) => ({
    ...s,
    opLoading: false,
    cantiereAndamenti: s.cantiereAndamenti.filter((x) => toId(x.id) !== toId(id)),
  })),

  on(
    CapitoliActions.createCantiereAndamentoFailure,
    CapitoliActions.updateCantiereAndamentoFailure,
    CapitoliActions.deleteCantiereAndamentoFailure,
    (s, { error }) => ({
      ...s,
      opLoading: false,
      opError: error,
    })
  ),

  on(CapitoliActions.loadExtraByCantiereId, (s) => ({
    ...s,
    extrasLoading: true,
    extrasError: null,
  })),

  on(CapitoliActions.loadExtraByCantiereIdSuccess, (s, { items }) => ({
    ...s,
    extrasLoading: false,
    extras: items,
  })),

  on(CapitoliActions.loadExtraByCantiereIdFailure, (s, { error }) => ({
    ...s,
    extrasLoading: false,
    extrasError: error,
  })),

  on(
    CapitoliActions.createExtra,
    CapitoliActions.updateExtra,
    CapitoliActions.deleteExtra,
    (s) => ({
      ...s,
      opLoading: true,
      opError: null,
    })
  ),

  on(
    CapitoliActions.createExtraSuccess,
    CapitoliActions.updateExtraSuccess,
    CapitoliActions.deleteExtraSuccess,
    (s) => ({
      ...s,
      opLoading: false,
    })
  ),

  on(
    CapitoliActions.createExtraFailure,
    CapitoliActions.updateExtraFailure,
    CapitoliActions.deleteExtraFailure,
    (s, { error }) => ({
      ...s,
      opLoading: false,
      opError: error,
    })
  ),
  on(CapitoliActions.loadIncassiByCantiereId, (s) => ({
    ...s,
    incassiLoading: true,
    incassiError: null,
  })),

  on(CapitoliActions.loadIncassiByCantiereIdSuccess, (s, { items }) => ({
    ...s,
    incassiLoading: false,
    incassi: items,
  })),

  on(CapitoliActions.loadIncassiByCantiereIdFailure, (s, { error }) => ({
    ...s,
    incassiLoading: false,
    incassiError: error,
  })),

  on(
    CapitoliActions.createIncasso,
    CapitoliActions.updateIncasso,
    CapitoliActions.deleteIncasso,
    (s) => ({
      ...s,
      opLoading: true,
      opError: null,
    })
  ),

  on(
    CapitoliActions.createIncassoSuccess,
    CapitoliActions.updateIncassoSuccess,
    CapitoliActions.deleteIncassoSuccess,
    (s) => ({
      ...s,
      opLoading: false,
    })
  ),

  on(
    CapitoliActions.createIncassoFailure,
    CapitoliActions.updateIncassoFailure,
    CapitoliActions.deleteIncassoFailure,
    (s, { error }) => ({
      ...s,
      opLoading: false,
      opError: error,
    })
  ),
  );
